import './casino.css'

import { useModal } from '@ebay/nice-modal-react'
import { useNavigate, useParams, useSearch } from '@tanstack/router'
import { useEffect, useMemo, useState } from 'react'

import platform_filter from '../assets/casino/platform-filter.webp'
import {
  CategoryContent,
  Favorites,
  GameCategories,
  GamesContainer,
  PageProgress,
  PlatformBanner,
  Providers,
  SearchBar,
} from '../components/casino'
import {
  AnnouncementCarousel,
  KycBonus,
  Loading,
  PlatformFilterModal,
  Spinner,
  WebTitle,
} from '../components/common'
import { DesktopLayout, MobileLayout } from '../components/layouts'
import useDimensions from '../hooks/useDimensions'
import useGames from '../hooks/useGames'
import { useBoundStore } from '../store/store'
import type { ApiResponse } from '../types/api'
import type { Game } from '../types/game'
import { api } from '../utils/api-client'

const Casino = () => {
  const { isDesktop } = useDimensions()
  const categories = useBoundStore(state => state.casino.categories)
  const clearCategoryGames = useBoundStore(
    state => state.casino.clearCategoryGames,
  )
  const allGames = useBoundStore(state => state.casino.allGames)
  const username = useBoundStore(state => state.user.username)
  const isUserLoading = useBoundStore(state => state.user.isLoading)
  const navigate = useNavigate({ from: '/' })
  const { category } = useParams({ from: '/casino/$category' })
  const { platform, error, message } = useSearch({
    from: '/casino/$category',
  })
  const { loadMoreGames, loadMoreLoading } = useGames()
  const [favorites, setFavorites] = useState<Game[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showFilters, setShowFilters] = useState(false)
  const platformFilterModal = useModal(PlatformFilterModal)

  const categoryKey = useMemo(() => {
    return (
      categories.find(
        item =>
          item.category.toLowerCase().replace(' ', '-') ===
          category.toLowerCase(),
      )?.id ?? '15663'
    )
  }, [category, categories])

  useEffect(() => {
    if (error && message) {
      throw new Error(`${message} (${error})`)
    }
  }, [platform, error, message])

  // First fetch to have the number of favorites
  useEffect(() => {
    if (!username) return
    api
      .post<ApiResponse<Game[]>>('/ajax_data.php', { type: 'get_collect_game' })
      .then(res => {
        const { status, info } = res.data
        if (status === 1) {
          setFavorites(info)
        } else {
          setFavorites([])
        }
        setIsLoading(false)
      })
      .catch(err => {
        console.error(err)
        setFavorites([])
      })
  }, [username])

  // Need to fetch updated list when going to favorites
  useEffect(() => {
    if (!username) return
    if (categoryKey !== 'favorites') return
    setIsLoading(true)
    api
      .post<ApiResponse<Game[]>>('/ajax_data.php', { type: 'get_collect_game' })
      .then(res => {
        const { status, info } = res.data
        if (status === 1) {
          setFavorites(info)
        } else {
          setFavorites([])
        }
        setIsLoading(false)
      })
      .catch(err => {
        console.error(err)
        setFavorites([])
        setIsLoading(false)
      })
  }, [username, categoryKey])

  const loadMore = () => {
    loadMoreGames(categoryKey, platform ?? '', false)
  }

  useEffect(() => {
    if (
      (categoryKey === 'recent_played' && isUserLoading) ||
      categoryKey === 'favorites' ||
      allGames?.[categoryKey]?.length ||
      !categories.length
    )
      return
    loadMore()
  }, [isUserLoading, categories.length])

  const selectedCategory = useMemo(() => {
    if (!categories.length || categoryKey === 'favorites') return null
    return categories.find(c => c.id === categoryKey)
  }, [categoryKey, categories])

  const totalCount = useMemo(() => {
    if (categoryKey === 'favorites') return favorites.length
    return Number(
      platform
        ? selectedCategory?.providers?.[platform]
        : selectedCategory?.count,
    )
  }, [categoryKey, platform, categories, favorites])

  const games = useMemo(() => {
    if (categoryKey === 'favorites') return favorites
    return allGames?.[categoryKey] ?? []
  }, [allGames, favorites, categoryKey, platform])

  if (isDesktop) {
    return (
      <DesktopLayout>
        <KycBonus />
        <div className="pb-6">
          <WebTitle />
          <PlatformBanner />
          <AnnouncementCarousel />
          {categoryKey !== 'favorites' && (
            <Providers
              category={categoryKey}
              selectedPlatform={platform ?? ''}
              setSelectedPlatform={platform => {
                loadMoreGames(categoryKey, platform, true)
                navigate({
                  to: '/casino/$category',
                  params: { category },
                  search: { platform },
                })
              }}
              moreProviders={() => {
                if (!categories) return
                platformFilterModal.show({
                  category: selectedCategory,
                  selectedPlatform: platform ?? '',
                  setSelectedPlatform: platform => {
                    loadMoreGames(categoryKey, platform, true)
                    navigate({
                      to: '/casino/$category',
                      params: { category },
                      search: {
                        platform: platform ? platform : undefined,
                      },
                    })
                  },
                })
              }}
            />
          )}
          <div className="flx-btw-ctr w-full bg-primary sticky z-10 mb-3 px-[90px] py-0 left-0 top-[60] 3xl:px-[150px] 3xl:py-0 4xl:px-[250px] 4xl:py-0">
            <GameCategories
              category={categoryKey}
              setCategory={newCategory => {
                // Clear current category games if there is platform selected
                if (platform) {
                  clearCategoryGames(categoryKey)

                  if (categoryKey === newCategory.id) {
                    loadMoreGames(newCategory.id, '', true)
                  }
                }
                if (!allGames?.[newCategory.id]?.length) {
                  loadMoreGames(newCategory.id, '', false)
                }
                navigate({
                  to: '/casino/$category',
                  params: {
                    category: newCategory.category
                      .toLowerCase()
                      .replace(' ', '-'),
                  },
                  search: { platform: undefined },
                })
              }}
              favorites={favorites}
              setShowFilters={setShowFilters}
              showFilters={showFilters}
            />
            <div className="gap-3 flex max-lg:mb-4 max-lg:px-2 max-lg:py-0 xl:flex-1">
              <SearchBar />
              {categoryKey !== 'favorites' && (
                <div
                  className="bg-highlight min-w-[53px] h-9 flex items-center justify-center rounded-md xl:transition-all xl:duration-[0.2s] xl:ease-[ease-in-out] xl:hover:opacity-80 cursor-pointer"
                  onClick={() => {
                    if (!categories) return
                    platformFilterModal.show({
                      category: selectedCategory,
                      selectedPlatform: platform ?? '',
                      setSelectedPlatform: platform => {
                        loadMoreGames(categoryKey, platform, true)
                        navigate({
                          to: '/casino/$category',
                          params: { category },
                          search: {
                            platform: platform ? platform : undefined,
                          },
                        })
                      },
                    })
                  }}>
                  <img
                    src={platform_filter}
                    alt="Platform Filter"
                    className="w-[35px] aspect-150/91"
                  />
                </div>
              )}
            </div>
          </div>
          {isLoading ? (
            <Spinner />
          ) : (
            <GamesContainer
              key={categoryKey + platform}
              categoryKey={categoryKey}
              games={games}
              favorites={favorites}
              setFavorites={setFavorites}
              loadMoreLoading={loadMoreLoading}
            />
          )}
          {categoryKey !== 'favorites' && games.length < totalCount && (
            <PageProgress
              current={games.length}
              total={totalCount}
              isLoading={loadMoreLoading}
              loadMore={loadMore}
            />
          )}
          {selectedCategory && <CategoryContent category={categoryKey} />}
        </div>
      </DesktopLayout>
    )
  }

  return (
    <MobileLayout bodyStyle={{ overflowX: 'hidden', height: '100vh' }}>
      <KycBonus />

      <div className="pb-6">
        <WebTitle />
        {favorites.length > 0 && (
          <Favorites
            category={categoryKey}
            onClick={() =>
              navigate({
                to: '/casino/$category',
                params: { category: 'favoritos' },
                search: { platform: undefined },
              })
            }
            noOfFavorites={favorites.length}
          />
        )}
        <PlatformBanner />
        <AnnouncementCarousel />
        {categoryKey !== 'favorites' && (
          <Providers
            category={categoryKey}
            selectedPlatform={platform ?? ''}
            setSelectedPlatform={platform => {
              loadMoreGames(categoryKey, platform, true)
              navigate({
                to: '/casino/$category',
                params: { category },
                search: { platform },
              })
            }}
            moreProviders={() => {
              if (!categories) return
              platformFilterModal.show({
                category: selectedCategory,
                selectedPlatform: platform ?? '',
                setSelectedPlatform: platform => {
                  loadMoreGames(categoryKey, platform, true)
                  navigate({
                    to: '/casino/$category',
                    params: { category },
                    search: {
                      platform: platform ? platform : undefined,
                    },
                  })
                },
              })
            }}
          />
        )}
        <GameCategories
          category={categoryKey}
          setCategory={newCategory => {
            // Clear current category games if there is platform selected
            if (platform) {
              clearCategoryGames(categoryKey)

              if (categoryKey === newCategory.id) {
                loadMoreGames(newCategory.id, '', true)
              }
            }
            if (!allGames?.[newCategory.id]?.length) {
              loadMoreGames(newCategory.id, '', false)
            }
            navigate({
              to: '/casino/$category',
              params: {
                category: newCategory.category.toLowerCase().replace(' ', '-'),
              },
              search: { platform: undefined },
            })
          }}
          favorites={favorites}
          setShowFilters={setShowFilters}
          showFilters={showFilters}
        />
        {isLoading ? (
          <Loading />
        ) : (
          <div>
            {showFilters && (
              <div className="gap-2 flex max-lg:mb-4 max-lg:px-2 max-lg:py-0 xl:flex-1">
                <SearchBar />
                {categoryKey !== 'favorites' && (
                  <div
                    className="min-w-[48px] bg-highlight h-8 flex items-center justify-center text-secondary-txt rounded-md"
                    onClick={() => {
                      if (!categories) return
                      platformFilterModal.show({
                        category: selectedCategory,
                        selectedPlatform: platform ?? '',
                        setSelectedPlatform: platform => {
                          loadMoreGames(categoryKey, platform, true)
                          navigate({
                            to: '/casino/$category',
                            params: { category },
                            search: {
                              platform: platform ? platform : undefined,
                            },
                          })
                        },
                      })
                    }}>
                    <img
                      src={platform_filter}
                      alt="Platform Filter"
                      className="w-[30px] aspect-150/91"
                    />
                  </div>
                )}
              </div>
            )}
            <GamesContainer
              key={categoryKey + platform}
              categoryKey={categoryKey}
              games={games}
              favorites={favorites}
              setFavorites={setFavorites}
              loadMoreLoading={loadMoreLoading}
            />
          </div>
        )}
        {categoryKey !== 'favorites' && games?.length < totalCount && (
          <PageProgress
            current={games.length}
            total={selectedCategory?.count ?? 0}
            isLoading={loadMoreLoading}
            loadMore={loadMore}
          />
        )}
        {selectedCategory && <CategoryContent category={categoryKey} />}
      </div>
    </MobileLayout>
  )
}

export default Casino
